import React, { useEffect, useState } from "react";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";

const STORAGE_KEY = "motowear_welcome_dismissed";
const DELAY_MS = 1500;
const CODE = "WELCOME10";

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);
  const { setPromoCode, promoCode } = useCart();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setOpen(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn("Clipboard write failed", e);
    }
  };

  const apply = () => {
    setPromoCode(CODE);
    setApplied(true);
    setTimeout(() => { close(); }, 2200);
  };

  if (!open) return null;
  const alreadyOnCart = promoCode === CODE;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.3s_ease-out]"
      onClick={close}
      data-testid="welcome-popup-overlay"
    >
      <div
        className="relative bg-white max-w-md w-full hairline-strong shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-popup"
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 text-black/50 hover:text-black"
          data-testid="welcome-popup-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="aspect-[16/9] bg-moto-gradient flex items-center justify-center">
          <div className="text-white text-center px-8">
            <div className="label-eyebrow text-white/80 mb-3">New here?</div>
            <div className="font-display text-6xl tracking-tighter leading-none">10% off.</div>
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-sm text-black/70 leading-relaxed">
            Welcome to MotoWear. Use this code at checkout for <strong>10% off</strong> your first order.
          </p>
          <div className="mt-6 flex items-stretch hairline-strong">
            <div
              className="flex-1 px-5 py-4 font-display text-2xl tracking-[0.3em] text-center select-all bg-[#FAFAFA]"
              data-testid="welcome-popup-code"
            >
              {CODE}
            </div>
            <button
              onClick={copy}
              className="px-5 bg-black text-white label-eyebrow flex items-center gap-2 hover:bg-[#FF3B30] transition-colors"
              data-testid="welcome-popup-copy"
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
          {!alreadyOnCart && !applied && (
            <button
              onClick={apply}
              className="mt-3 w-full bg-black text-white py-3 label-eyebrow flex items-center justify-center gap-2 hover:bg-[#FF3B30] transition-colors"
              data-testid="welcome-popup-apply"
            >
              <Sparkles size={14} /> Apply to my cart
            </button>
          )}
          {(applied || alreadyOnCart) && (
            <div className="mt-3 w-full py-3 label-eyebrow border border-[#FF3B30] text-[#FF3B30] flex items-center justify-center gap-2" data-testid="welcome-popup-applied">
              <Check size={14} /> Applied — it will be used at checkout
            </div>
          )}
          <button
            onClick={close}
            className="mt-3 w-full bg-moto-gradient text-white py-4 label-eyebrow hover:bg-black hover:[background-image:none] transition-colors shadow-[0_6px_16px_rgba(255,59,48,0.35)]"
            data-testid="welcome-popup-shop"
          >
            Shop The Collection
          </button>
          <button
            onClick={close}
            className="mt-3 text-xs text-black/40 hover:text-black"
            data-testid="welcome-popup-dismiss"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
}
